import LinkTag from '@/src/component/LinkTag';
import { CompanyGeneralLinks } from '@/data/superstack/companies/CompanyGeneralLinks';
import { Button, ButtonBlock } from '@/src/component/superstack/base/Button';
import styles from '@/styles/superstack/companies/CompanyBanners.module.scss';
import classNames from 'classnames/bind';
import ImgLogo from '@/asset/superstack/svgs/logo_wishket_jobs_sm.svg';
import ImgStackMatchMobile from '@/asset/superstack/svgs/img-stack-match-m.svg';
import ImgProfileMatch from '@/asset/superstack/svgs/img-profile-match.svg';
import ImgJobDelivery from '@/asset/superstack/svgs/img-job-delivery.svg';
import ImgProfileDocument from '@/asset/superstack/svgs/img-profile-document.svg';
import IconArrowFilledRight from '@/asset/superstack/svgs/icon-arrow-filled-right.svg';
import IconPlus from '@/asset/superstack/svgs/plus.svg';
import IconMatch from '@/asset/superstack/svgs/icon-match.svg';
import IconProfile from '@/asset/superstack/svgs/icon-profile.svg';
import IconStars from '@/asset/superstack/svgs/icon-stars-lg.svg';
import ImgBarItemCompleted from '@/asset/superstack/svgs/img-bar-item-completed.svg';
import ImgBarItem from '@/asset/superstack/svgs/img-bar-item.svg';
import IconCheck from '@/asset/superstack/svgs/icon-check.svg';
import IconArrowRightLong from '@/asset/superstack/svgs/icon-arrow-right-long.svg';
import useCompany from '@/src/utils/hooks/superstack/useCompany';

const cx = classNames.bind(styles);

const CompanyStacksCTA = ({ bannerMT = '0', bannerHeight = 'auto' }) => {
  // 노출 조건: 로그인 && (기업회원 아니거나 기업 스택 없음)
  return (
    <>
      <section className={cx('background-upper')} style={{ height: bannerHeight }}>
        <div className={cx(['copy', 'pc'])}>
          <div className={cx('content-wrapper')}>
            <p className={cx('content')}>
              구체적인 프로필, 회사와 맞는 기술 스택을 가진 개발자를 만날 수 있는 가장 좋은 플랫폼
            </p>
            <ImgLogo />
          </div>
          <h5 className={cx('title')}>원하는 인재에게 회사의 기술 스택을 먼저 알려주세요.</h5>
        </div>
        <div className={cx(['copy', 'mobile'])}>
          <span>
            <ImgStackMatchMobile />
          </span>
          <h5 className={cx('title')}>
            원하는 인재에게 <br />
            회사의 기술 스택을
            <br /> 먼저 알려주세요.
          </h5>
          <LinkTag
            href={CompanyGeneralLinks().find((link) => link.name === 'company_stacks_router')?.url}
            isNextPage={true}
          >
            <ButtonBlock
              mode={'borderless'}
              size={'xl'}
              style={{ width: '100%', padding: '14px 0', marginTop: '20px' }}
            >
              우리 회사 기술 스택 추가하기
            </ButtonBlock>
          </LinkTag>
        </div>
      </section>
      <section className={cx(['banner', 'pc'])} style={{ marginTop: bannerMT }}>
        <div className={cx('content-container')}>
          <div className={cx('content-item')}>
            <ImgProfileMatch />
            <div className={cx('text-wrapper')}>
              <p className={cx('title')}>기술 스택이 딱 맞는 개발자 프로필을 받아보세요.</p>
              <p className={cx('subtitle')}>
                회사 기술 스택을 입력해보세요. 기술 스택이 일치하는
                <br />
                개발자 프로필을 이메일로 받아볼 수 있어요.
              </p>
            </div>
          </div>
          <div className={cx('content-item')}>
            <ImgJobDelivery />
            <div className={cx('text-wrapper')}>
              <p className={cx('title')}>회사의 채용 공고를 개발자들에게 대신 전달해 드려요.</p>
              <p className={cx('subtitle')}>
                공고와 일치하는 기술 스택을 가진 개발자에게
                <br />
                공고를 추천하고, 전달해 드려요.
              </p>
            </div>
          </div>
          <div className={cx('content-item')}>
            <ImgProfileDocument />
            <div className={cx('text-wrapper')}>
              <p className={cx('title')}>회사를 정확하게 설명하고, 잠재 인력풀을 만나세요.</p>
              <p className={cx('subtitle')}>
                회사의 프로덕트부터 사용하고 있는 기술까지
                <br />
                정확한 정보를 입력하고 딱 맞는 인력풀을 만나세요.
              </p>
            </div>
          </div>
        </div>
        <LinkTag
          href={CompanyGeneralLinks().find((link) => link.name === 'company_stacks_router')?.url}
          isNextPage={true}
        >
          <ButtonBlock mode={'filled'} size={'lg'}>
            <span
              style={{
                display: 'flex',
                alignItems: 'center',
                gap: '8px',
                justifyContent: 'center',
              }}
            >
              <IconPlus width={15} height={15} fill={'#ffffff'} />
              <p>우리 회사 기술 스택 추가하기</p>
            </span>
          </ButtonBlock>
        </LinkTag>
      </section>
    </>
  );
};

const CompanyStacksAdminCTA = ({ companyId, bannerMT = '0', bannerHeight = 'auto' }) => {
  const { data: { data: company = {} } = {} } = useCompany(companyId);

  const hasCompanyStacks = company?.company_stacks?.length > 0;
  const hasService = company?.has_service;

  if (!company.id) {
    return null;
  } else if (hasCompanyStacks && hasService) {
    return null;
  }

  return (
    <>
      <section className={cx('background-upper')} style={{ height: bannerHeight }}>
        <div className={cx(['copy', 'pc'])}>
          <div className={cx('content-wrapper')}>
            <p className={cx('content')}>원하는 인재에게 회사의 기술 스택을 먼저 알려주세요 🤗</p>
            <ImgLogo />
          </div>
          <h5 className={cx('title')}>
            필요한 순간에 우리 회사에 맞는 기술 스택을 가진 인재를 만날 수 있습니다.
          </h5>
        </div>
        <div className={cx(['copy', 'mobile'])}>
          <span>
            <ImgStackMatchMobile />
          </span>
          <h5 className={cx('title')}>
            원하는 인재에게 <br />
            회사의 기술 스택을
            <br /> 먼저 알려주세요 🤗
          </h5>
          <LinkTag
            href={CompanyGeneralLinks().find((link) => link.name === 'company_stacks_router')?.url}
            isNextPage={true}
          >
            <ButtonBlock
              mode={'borderless'}
              size={'xl'}
              style={{ width: '100%', padding: '14px 0', marginTop: '20px' }}
            >
              우리 회사 기술 스택 추가하기
            </ButtonBlock>
          </LinkTag>
        </div>
        <section className={cx(['progress-bar', 'pc'])}>
          <div className={cx(['bar-item', 'completed'])}>
            <p className={cx('title')}>회사 소개 완료</p>
            <div className={cx('bar')}>
              <ImgBarItemCompleted />
            </div>
            <section className={cx('checklist')}>
              <p className={cx('check-item')}>
                <IconCheck width={8} height={6} fill={'#ffffff'} />
                기업회원 가입
              </p>
              <p className={cx('check-item')}>
                <IconCheck width={8} height={6} fill={'#ffffff'} />
                기업회원 시작하기
              </p>
            </section>
          </div>
          <div className={cx(['bar-item', hasCompanyStacks ? 'completed' : ''])}>
            <p className={cx(['title'])}>사용 중인 기술을 알려주세요.</p>
            <div className={cx('bar')}>
              {hasCompanyStacks ? <ImgBarItemCompleted /> : <ImgBarItem />}
            </div>
            <section className={cx('checklist')}>
              <LinkTag
                href={
                  hasCompanyStacks
                    ? null
                    : CompanyGeneralLinks(company.id).find(
                        (link) => link.name === 'company_manage_stacks_form',
                      )?.url
                }
                isNextPage={true}
              >
                <p className={cx('check-item')}>
                  {hasCompanyStacks ? (
                    <IconCheck width={8} height={6} fill={'#ffffff'} />
                  ) : (
                    <IconArrowRightLong width={10} height={7} fill={'#ffffff'} />
                  )}
                  회사 기술 스택 입력
                </p>
              </LinkTag>
            </section>
          </div>
          <div className={cx(['bar-item', hasCompanyStacks && hasService ? 'completed' : ''])}>
            <p className={cx(['title'])}>운영하고 있는 프로덕트를 자랑해주세요.</p>
            <div className={cx('bar')}>
              {hasCompanyStacks && hasService ? <ImgBarItemCompleted /> : <ImgBarItem />}
            </div>
            <section className={cx('checklist')}>
              <LinkTag
                href={
                  hasCompanyStacks && hasService
                    ? null
                    : CompanyGeneralLinks(company.id).find(
                        (link) => link.name === 'company_service_form',
                      )?.url
                }
                isNextPage={true}
              >
                <p className={cx('check-item')}>
                  {hasCompanyStacks && hasService ? (
                    <IconCheck width={8} height={6} fill={'#ffffff'} />
                  ) : (
                    <IconArrowRightLong width={10} height={7} fill={'#ffffff'} />
                  )}
                  프로덕트 추가
                </p>
              </LinkTag>
            </section>
          </div>
        </section>
      </section>
      <section className={cx(['banner', 'pc'])} style={{ marginTop: bannerMT, padding: 0 }}>
        <div className={cx('content-box-wrapper')}>
          <div className={cx('content-box')}>
            <p>다음 정보를 채워주세요. 위시켓 잡스에서 더 적합한 개발자를 만나실 수 있습니다.</p>
            <div className={cx('cta-wrapper')}>
              {!hasCompanyStacks && (
                <>
                  <div className={cx('cta-item')} style={{ marginBottom: '16px' }}>
                    <div className={cx('text-wrapper')}>
                      <IconArrowFilledRight />
                      <span className={cx('text')}>회사 기술 스택 입력</span>
                    </div>
                    <LinkTag
                      href={
                        CompanyGeneralLinks(company.id).find(
                          (link) => link.name === 'company_manage_stacks_form',
                        )?.url
                      }
                      isNextPage={true}
                    >
                      <Button mode={'filled'}>스택 입력하러 가기</Button>
                    </LinkTag>
                  </div>
                  <div style={{ width: '100%', border: '1px solid #EEEEEE' }} />
                </>
              )}
              {!hasService && (
                <div className={cx('cta-item')} style={{ marginTop: '16px' }}>
                  <div className={cx('text-wrapper')}>
                    <IconArrowFilledRight />
                    <span className={cx('text')}>프로덕트 추가</span>
                  </div>
                  <LinkTag
                    href={
                      CompanyGeneralLinks(company.id).find(
                        (link) => link.name === 'company_service_form',
                      )?.url
                    }
                    isNextPage={true}
                  >
                    <Button mode={'filled'}>프로덕트 추가하러 가기</Button>
                  </LinkTag>
                </div>
              )}
              {(hasCompanyStacks || hasService) && (
                <div className={cx('text-item')} style={{ marginTop: '24px' }}>
                  <IconStars />
                  <p className={cx('text')}>
                    거의 다 왔어요! 마지막 정보를 입력하고 원하는 개발자를 만나보세요.
                  </p>
                </div>
              )}
            </div>
          </div>
          {!hasCompanyStacks && (
            <section className={cx('rolling-part')}>
              <IconMatch />
              <p className={cx('title')}>
                기술 스택을 입력하고,
                <br />
                맞는 개발자를 찾아보세요.
              </p>
              <p className={cx('text')}>
                기술 스택 프로필을 등록하고, 회사와 맞는
                <br />
                스택을 가진 개발자의 지원만 받아보세요.
              </p>
            </section>
          )}
          {hasCompanyStacks && (
            <section className={cx('rolling-part')}>
              <IconProfile />
              <p className={cx('title')}>
                회사를 정확하게 설명하고,
                <br />
                잠재 인력풀을 만나세요.
              </p>
              <p className={cx('text')}>
                회사의 프로덕트부터 사용하고 있는 기술까지
                <br />
                정확한 정보를 입력하고 딱 맞는 인력풀을 만나세요.
              </p>
            </section>
          )}
        </div>
      </section>
    </>
  );
};

export { CompanyStacksCTA, CompanyStacksAdminCTA };
